import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ListViewDataResult,
  PagerSettings,
} from '@progress/kendo-angular-listview';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import {
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  SelectablePrimitive,
} from '@shared';
import { RestaurantItemService } from '../../services';

@Component({
  selector: 'app-custom-filter',
  templateUrl: './custom-filter.component.html',
  styleUrl: './custom-filter.component.scss',
})
export class CustomFilterComponent {
  @Input() field!: string;
  @Input() type!: string;
  @Input() view!: string;
  @Input() productType!: string;
  @Input() preselected: string[] = [];
  @Input() restaurantId?: string;

  @Output() filterChanged: EventEmitter<string[]> = new EventEmitter<
    string[]
  >();

  @Input()
  filter?: GridStateCompositeFilterDescriptor =
    new GridStateCompositeFilterDescriptor();

  state!: GridState;

  tierId: string = '';
  allSelected: boolean = false;
  data!: ListViewDataResult;
  includes: string = '';
  itemSearch: Subject<string> = new Subject<string>();
  pagerSettings: PagerSettings = {
    buttonCount: 3,
    info: true,
    type: 'numeric',
    previousNext: true,
  };
  selectableItems: SelectablePrimitive<string>[] = [];
  masterSelected: any[] = [];
  pageSize: number = 20;

  constructor(
    private loaderService: LoaderService,
    private messageService: MessageService,
    private service: RestaurantItemService,
  ) {}

  ngOnInit() {
    this.prepopulatePreselected();
    this.subscribeToAutocomplete();
    this.buildDefaultState();
    this.fetchItems();
  }

  prepopulatePreselected() {
    this.masterSelected = [...this.preselected];
  }

  buildDefaultState() {
    this.state = new GridState(
      this.pageSize,
      [{ field: this.field, dir: 'asc' }],
      this.tierId,
      this.productType,
    );

    this.state.filter = new GridStateCompositeFilterDescriptor();

    // remove the current field, otherwise it will not allow to be changed
    if (this.filter) {
      const removedField = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(this.filter, this.field),
      );
      if (removedField) {
        this.state.filter = removedField;
      }
    }

    this.state.filter = this.state.filter.and({
      field: this.field,
      operator: 'isnotnull',
    });
  }

  fetchItems(state?: GridState) {
    if (!state) state = this.state;
    this.loaderService.show();
    this.service
      .getDistinctItems(state, this.view, this.field, this.restaurantId)
      .subscribe({
        next: (listViewResult) => {
          this.data = listViewResult;
          this.selectableItems = this.data.data.map((item) => {
            const isSelected =
              this.allSelected ||
              this.masterSelected.includes(item[this.field]);
            return new SelectablePrimitive<string>(
              item[this.field],
              isSelected,
            );
          });

          this.updateSelected();

          this.loaderService.hide();
        },
        error: (err) => {
          this.messageService.error(err);
          this.loaderService.hide();
        },
      });
  }

  updateSelected() {
    this.selectableItems.forEach((item) => {
      if (item.selected) {
        if (!this.masterSelected.includes(item.value)) {
          this.masterSelected.push(item.value);
        }
      } else {
        this.masterSelected = this.masterSelected.filter(
          (selected) => selected !== item.value,
        );
      }
    });

    this.filterChanged.emit(this.masterSelected);
  }

  handleFilter(item: string) {
    this.itemSearch.next(item);
  }

  handleScroll() {
    if (this.state.take! >= this.data.total) return;
    this.state.take! += this.pageSize;
    this.fetchItems(this.state);
  }

  subscribeToAutocomplete() {
    this.itemSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.includes = searchText;
          this.state.resetPager(this.pageSize);
          this.buildDefaultState();
          if (this.includes) {
            this.state.filter = this.state.filter?.and({
              field: this.field,
              ignoreCase: true,
              operator: this.type == 'string' ? 'contains' : 'eq',
              value: this.includes,
            });
          }

          this.fetchItems(this.state);
        }),
      )
      .subscribe();
  }

  toggleSelect(dataItem: SelectablePrimitive<string>) {
    dataItem.selected = !dataItem.selected;

    this.updateSelected();
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;
    this.state.skip = 0;
    this.state.take = this.data.total;
    this.fetchItems(this.state);
  }
}
